import type { SessionService } from '@efdigital/ll-addon-auth';
import { CoachCard } from '@efdigital/ll-addon-coaching';
import { getStatusIcon } from '@efdigital/ll-addon-coaching/utils/coaching-sessions/coach-partnership-status';
import type { CoachProfile } from '@efdigital/ll-addon-data';
import { Heading } from '@efdigital/ll-addon-ui';
import type {
  CoachMatch,
  FallbackRelevance,
} from '@efdigital/ll-cdk-coaching-ai-matching-api-types';
import { EmptyState } from '@efdigital/ll-xceed-v2/components/application/empty-state';
import { Spinner } from '@efdigital/ll-xceed-v2/components/application/spinner.gts';
import { CoachPartnershipActions } from '@efdigital/ll-xceed-v2/components/coaching/coachee/coach-partnership-actions.gts';
import {
  CoachesBrowse,
  type CoachProfileWithRelevance,
} from '@efdigital/ll-xceed-v2/components/coaching/coachee/coaches-browse.gts';
import type CoachingCoacheeCohortCoachesIndexController from '@efdigital/ll-xceed-v2/controllers/coaching/coachee/cohort/coaches/index';
import { queryCoachProfilesByUserIds } from '@efdigital/ll-xceed-v2/data/builders/coaching/coach-profiles';
import type { CoachesIndexModel } from '@efdigital/ll-xceed-v2/routes/coaching/coachee/cohort/coaches/index';
import type CoacheeService from '@efdigital/ll-xceed-v2/services/coachee';
import { coachStatusBadgeClass } from '@efdigital/ll-xceed-v2/utils/coaching/partnership-status-display';
import { PAGE_WRAPPER_CONTENT } from '@efdigital/ll-xceed-v2/utils/layout';
import { array, concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { Request } from '@warp-drive/ember';
import { pageTitle } from 'ember-page-title';
import { cell, resource, use } from 'ember-resources';

/**
 * Bounded retry delays (ms) for the coach-matches fetch itself failing outright
 * (network error, non-2xx). Total window: 14s across 4 retries, then show an error.
 */
const RETRY_DELAYS_MS = [1500, 2500, 4000, 6000];

/**
 * Flat relevance value the backend returns for every coach when it can't compute real
 * relevance for anyone yet (e.g. the coachee's onboarding answer embedding, generated
 * asynchronously right after quiz submission, hasn't finished yet) - the coach list itself
 * is still returned so there's always something to show. Typed against the shared
 * `FallbackRelevance` literal so this can't silently drift from the backend's value.
 */
const FALLBACK_RELEVANCE_VALUE: FallbackRelevance = 50;

/**
 * Bounded retry delays (ms) used while the response looks like the flat-fallback pattern
 * above, giving the coachee's embedding a chance to finish generating before settling for
 * an unranked list. Total window: 10s across 3 retries, then just show what we have.
 */
const FALLBACK_RETRY_DELAYS_MS = [2000, 3000, 5000];

function isFlatFallbackRelevance(matches: CoachMatch[]): boolean {
  return (
    matches.length > 0 &&
    matches.every((match) => match.relevance === FALLBACK_RELEVANCE_VALUE)
  );
}

interface CoachesIndexSignature {
  Args: {
    model: CoachesIndexModel;
    controller: CoachingCoacheeCohortCoachesIndexController;
  };
}

interface RelevanceMatchesState {
  matches: CoachMatch[];
  pending: boolean;
  failed: boolean;
}

function showRatingBadge(relevance: number): boolean {
  return relevance >= 70;
}

export default class CoachesIndex extends Component<CoachesIndexSignature> {
  @service declare session: SessionService;
  @service declare coachee: CoacheeService;

  @use relevanceMatches = resource<RelevanceMatchesState>(({ on }) => {
    const state = cell<RelevanceMatchesState>({
      matches: [],
      pending: true,
      failed: false,
    });
    const cohortId = this.args.model.cohort_id;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const attempt = async (
      delaysRemaining: number[],
      fallbackDelaysRemaining: number[]
    ): Promise<void> => {
      let matches: CoachMatch[];
      try {
        matches = await this.coachee.tryGetRelevanceMatches(cohortId);
      } catch (error) {
        console.error('[coach-matches] failed to fetch relevance', error);

        if (cancelled) {
          return;
        }

        const [delay, ...rest] = delaysRemaining;
        if (delay === undefined) {
          state.set({ matches: [], pending: false, failed: true });
          return;
        }

        timer = setTimeout(
          () => void attempt(rest, fallbackDelaysRemaining),
          delay
        );
        return;
      }

      if (cancelled) {
        return;
      }

      if (isFlatFallbackRelevance(matches)) {
        const [fallbackDelay, ...fallbackRest] = fallbackDelaysRemaining;
        if (fallbackDelay !== undefined) {
          timer = setTimeout(
            () => void attempt(delaysRemaining, fallbackRest),
            fallbackDelay
          );
          return;
        }
        console.warn(
          '[coach-matches] still receiving flat fallback relevance after retries, showing unranked list'
        );
      }

      state.set({ matches, pending: false, failed: false });
    };

    void attempt(RETRY_DELAYS_MS, FALLBACK_RETRY_DELAYS_MS);

    on.cleanup(() => {
      cancelled = true;
      clearTimeout(timer);
    });

    return () => state.current;
  });

  coachesWithRelevance(
    coachProfiles: CoachProfile[],
    relevanceMatches: CoachMatch[]
  ): CoachProfileWithRelevance[] {
    return coachProfiles.map((coachProfile) => ({
      coachProfile,
      relevance:
        relevanceMatches.find((match) => match.coachId === coachProfile.user)
          ?.relevance ?? 0,
    }));
  }

  get coachUserIds(): string[] {
    return this.relevanceMatches.matches.map((match) => match.coachId);
  }

  <template>
    {{pageTitle "Coaches"}}

    <div class={{PAGE_WRAPPER_CONTENT}}>
      {{#if this.relevanceMatches.pending}}
        <Spinner />
      {{else if this.relevanceMatches.failed}}
        <div class="alert alert-danger" role="alert">
          There was an error fetching the list of coaches. Please try again
          later. If the problem persists, please contact
          <a
            href="mailto:v-support@hultashridge.com"
          >v-support@hultashridge.com</a>.
        </div>
      {{else if this.coachUserIds.length}}
        <Request @query={{queryCoachProfilesByUserIds this.coachUserIds}}>
          <:content as |coachProfiles|>
            {{#let
              (this.coachesWithRelevance
                coachProfiles.data this.relevanceMatches.matches
              )
              as |pool|
            }}
              <Heading
                @size="sm"
                @text="Browse Coaches"
                @secondary="Apply filters to narrow down the list and find the perfect coach for you."
                class="mb-4"
              />

              <CoachesBrowse
                @pool={{pool}}
                @searchQuery={{@controller.searchQuery}}
                @sort={{@controller.validatedSort}}
                @specialty={{@controller.specialty}}
                @language={{@controller.language}}
                @industry={{@controller.industry}}
                @country={{@controller.country}}
                @onSearchQueryChange={{@controller.setSearchQuery}}
                @onSortChange={{@controller.setSort}}
                @onSpecialtyChange={{@controller.setSpecialty}}
                @onLanguageChange={{@controller.setLanguage}}
                @onIndustryChange={{@controller.setIndustry}}
                @onCountryChange={{@controller.setCountry}}
                class="mb-4"
              >
                <:matches as |matches|>
                  {{#if matches.length}}

                    <div class="row g-3 g-xl-4 align-items-stretch">
                      {{#each matches as |match|}}
                        {{#let
                          (@model.planner.getStatusForCoach
                            match.coachProfile.user
                          )
                          as |status|
                        }}
                          {{#let
                            (@model.planner.hasUncancelledChemistrySession
                              match.coachProfile.user
                            )
                            as |hasChemistrySession|
                          }}
                            {{#let
                              (getStatusIcon status hasChemistrySession)
                              as |icon|
                            }}
                              <div class="col-12 col-md-6 col-xxl-4">
                                <CoachCard
                                  class="h-100 w-100 ll-coach-browse-card"
                                  @coach={{match.coachProfile}}
                                  @cardClass="h-100 w-100 rounded-4 shadow-sm"
                                  @footerClass="d-flex flex-row justify-content-between gap-2 align-items-center bg-transparent border-top"
                                  @color={{icon.color}}
                                >
                                  <:badge>
                                    {{#if (showRatingBadge match.relevance)}}
                                      <span
                                        class="badge rounded-pill bg-body text-body border"
                                      >{{Math.round match.relevance}}% match</span>
                                    {{/if}}
                                    {{#if icon}}
                                      {{#let
                                        (coachStatusBadgeClass icon)
                                        as |badgeClass|
                                      }}
                                        <span class={{badgeClass}}>
                                          <iconify-icon
                                            icon="{{icon.icon}}"
                                            class="fs-6"
                                            inline
                                            aria-hidden="true"
                                          />
                                          {{icon.label}}
                                        </span>
                                      {{/let}}
                                    {{/if}}
                                  </:badge>
                                  <:actionsBack>
                                    <CoachPartnershipActions
                                      @planner={{@model.planner}}
                                      @coachUserId={{match.coachProfile.user}}
                                      @coachName={{concat
                                        match.coachProfile.firstName
                                        " "
                                        match.coachProfile.lastName
                                      }}
                                      @buttonClass="btn btn-sm rounded-pill text-nowrap"
                                      @collapseMultipleActions={{true}}
                                    />
                                  </:actionsBack>
                                  <:actionsFront>
                                    <LinkTo
                                      @route="coaching.coachee.cohort.coaches.coach"
                                      @models={{array
                                        @model.program_id
                                        @model.cohort_id
                                        match.coachProfile.user
                                      }}
                                      class="btn btn-outline-primary btn-sm rounded-pill d-inline-flex align-items-center justify-content-center gap-1 text-nowrap"
                                    >Full profile
                                      <iconify-icon
                                        icon="ph:arrow-right-bold"
                                        inline
                                        aria-hidden="true"
                                      /></LinkTo>
                                  </:actionsFront>
                                </CoachCard>
                              </div>
                            {{/let}}
                          {{/let}}
                        {{/let}}
                      {{/each}}
                    </div>
                  {{else}}
                    <EmptyState
                      @message="No coaches found that match your search criteria."
                    />
                  {{/if}}
                </:matches>
              </CoachesBrowse>
            {{/let}}
          </:content>
          <:loading>
            <Spinner />
          </:loading>
          <:error as |_error state|>
            <div class="alert alert-danger" role="alert">
              <p>Coach profiles could not be loaded.</p>
              <button
                type="button"
                class="btn btn-sm btn-outline-primary"
                {{on "click" state.retry}}
              >Try again</button>
            </div>
          </:error>
        </Request>
      {{else}}
        <Heading @size="sm" @text="Browse Coaches" class="mb-4" />
        <EmptyState
          @message="No coaches are available for your cohort yet. Please check back later."
        />
      {{/if}}
    </div>
  </template>
}
